// What a strategy document is CALLED on screen.
//
// The engine's vocabulary is ids — "fractional-kelly", "aleague", "shin" — and
// every screen that prints one has to turn it into words. One place, so the
// card, the editor and Today cannot end up calling the same thing three
// things.
//
// Nothing here decides anything: no defaults, no validation, no running. If a
// value arrives that this file has never heard of it is printed as itself
// rather than swallowed, because a strategy showing "squiggle-ensemble" is a
// missing label and a strategy showing nothing is a missing feature.

import { KNOWN_MODELS } from './engine/strategy.js';
import { SPORT_LABELS, sportLabel } from './engine/teams.js';
import { signedPct } from './format.js';
import { EVIDENCE_DAYS, verdictText } from './paper-trail.js';

/**
 * Probability sources the engine can run.
 *
 * This was the two that never leave the engine, because nothing on the device
 * had ever fetched a published state. It is now all of them: `lib/stores/
 * models.js` loads the states and binds them into `ctx.models`, and the ones
 * that still cannot run on a given day (no state for that sport, no
 * probabilities uploaded) say so per strategy rather than per model.
 */
export const RUNNABLE_MODELS = [...KNOWN_MODELS];

/** What the editor offers, in the order it offers it. Filtered to what the engine knows. */
export const EDITOR_MODELS = [
  'none', 'market-consensus', 'sharp-anchor', 'elo', 'dixon-coles', 'squiggle-ensemble', 'user-upload',
].filter((m) => KNOWN_MODELS.includes(m));

/**
 * Why a strategy with a model printed nothing, in Today's voice.
 *
 * `runStrategy` says `no model supplied for "elo"`, which is precise and
 * useless to a punter: it is the ENGINE's account of a missing argument, not
 * an answer to "where is my ticket". The statuses come from
 * `lib/stores/models.js`; a model still on its way is a different sentence
 * from one that was never published.
 */
export const MODEL_NOTES = {
  loading: 'Loading the model…',
  missing: 'No published model for this sport yet.',
  yours: 'Upload your probabilities to run this one.',
};

/**
 * "AFL, NRL, NBL and BBL". Plain English, no serial comma, and the words
 * rather than the ids, because this is read out loud on the pad.
 */
export function sportsList(sports) {
  const words = (sports ?? []).filter(Boolean).map(sportLabel);
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * Which of those to print, given every status this strategy is waiting on.
 *
 * "NO PUBLISHED MODEL FOR THIS SPORT YET" WAS A SHRUG. It is true of a board
 * of AFL matches whose Elo ratings have not landed, and it is true of a board
 * with no sport on it this model has ever been published for — and those are
 * different mornings. Both now name the sports, because the sports are the
 * whole of the answer and the app knows them both:
 *
 *   Elo Edge rates AFL, NRL, NBL and BBL; today's board is MLB and NFL.
 *   No published Elo yet for AFL and NRL.
 *
 * `coverage` is `{ name, covered, board }` — the strategy's own name, the
 * sports its model is published for (narrowed by the strategy's sport filter,
 * because a strategy that only ever bets the AFL does not "rate" the BBL), and
 * the sports actually on today's board. With any of them missing the old
 * sentence stands: a note that names nothing is better than one that names the
 * wrong thing.
 *
 * NOTE THE EMPTY STATUS LIST. `modelsNeededFor` asks for a state per (model,
 * sport) it could USE, so an empty list means not one sport on the board is one
 * this model covers — which used to print "Loading the model…" for ever.
 */
export function modelNote(statuses, model, coverage = {}) {
  if (model === 'user-upload') return MODEL_NOTES.yours;
  const list = statuses ?? [];
  const covered = coverage.covered ?? [];
  const board = coverage.board ?? [];
  const name = coverage.name ?? '';
  if (!list.length) {
    // Nothing was ever asked for. Either the board has not arrived yet, or it
    // has and this model does not cover any of it.
    if (!name || !covered.length || !board.length) return MODEL_NOTES.loading;
    return `${name} rates ${sportsList(covered)}; today’s board is ${sportsList(board)}.`;
  }
  if (list.some((s) => s === 'loading' || s === 'idle')) return MODEL_NOTES.loading;
  // States were asked for and none came back. The sports they were asked for
  // are the covered ones that are on the board, which is what to name.
  const waiting = covered.filter((s) => board.includes(s));
  const modelName = MODEL_LABELS[model] ?? titleCase(model);
  if (!waiting.length || !modelName) return MODEL_NOTES.missing;
  return `No published ${modelName} yet for ${sportsList(waiting)}.`;
}

/**
 * Why a strategy with no model printed nothing, in Today's voice.
 *
 * The engine's note here is `no leg cleared the edge and EV thresholds`,
 * which is true but doesn't say how far it got. "Found nothing" on a day
 * with one match and "found nothing" on a day with thirty are different
 * answers — one is the sport filter doing its job, the other is a market
 * that just didn't move. `candidates` is every selection the engine looked
 * at after filters and rules, before the edge/EV cut, so its length and best
 * edge are the honest account of how close this strategy came.
 */
export function noLegNote(candidates) {
  const list = candidates ?? [];
  if (!list.length) return 'Nothing on today’s board fits this strategy.';
  const legs = list.length === 1 ? '1 leg' : `${list.length} legs`;
  const best = Math.max(...list.map((c) => c.edge));
  return `No leg cleared the edge test today: looked at ${legs}, the best edge was ${signedPct(best)}.`;
}

/**
 * What a strategy has actually done on Today, in one line — `evidenceFor`'s
 * numbers said out loud under a strategy card and beside a pick.
 *
 *   Last 30 days: 7 of 25 landed · avg CLV +1.2% over 118 legs · Too few to tell
 *
 * IT COUNTS SETTLED TICKETS, NOT PROPOSED ONES. The window ends today, and
 * today's tickets are all still pending at the time anybody reads this, so
 * "7 of 41" against everything proposed would print a worse strike rate every
 * morning and correct itself overnight. `decided` is the denominator the
 * verdict is judged on too, which is the other half of the reason.
 *
 * The verdict goes last because it is the thing that decides whether the
 * numbers in front of it mean anything — and it comes off the line entirely
 * (`verdict: false`) where the screen stamps it instead.
 *
 * Last, when `evidence.vsClassic` says the comparison is safe to print, comes
 * how this strategy's CLV compares with Classic's over the same window —
 * "CLV +1.4% vs Classic", or "CLV −0.6% vs Classic" when it is behind.
 * Classic's own line carries no `vsClassic` at all, so it never grows one.
 */
export function evidenceLine(evidence, { verdict = true } = {}) {
  const e = evidence ?? {};
  const days = Number.isFinite(e.days) ? e.days : EVIDENCE_DAYS;

  // Nothing to count. Not "0 of 0 landed", which reads as a strategy that has
  // been tried and failed rather than one that has not been tried.
  if (!e.proposed) return 'No proposals settled yet · evidence builds from tomorrow.';
  if (!e.decided) return `Last ${days} days: ${e.proposed} proposed, none settled yet`;

  const parts = [`Last ${days} days: ${e.won} of ${e.decided} landed`];
  if (e.legsWithClose && Number.isFinite(e.avgClv)) {
    parts.push(`avg CLV ${signedPct(e.avgClv)} over ${e.legsWithClose} leg${e.legsWithClose === 1 ? '' : 's'}`);
  }
  if (verdict) parts.push(verdictText(e.verdict));
  if (e.vsClassic?.comparable) parts.push(`CLV ${signedPct(e.vsClassic.clvDelta)} vs Classic`);
  return parts.join(' · ');
}

/**
 * What taking mispriced prices costs, said once.
 *
 * A book that only ever sees you take the price it got wrong works out what you
 * are doing and cuts your maximum stake, and that is the ordinary end of a
 * profitable account rather than a surprise. The strategy's explainer says it
 * and the Mispriced glossary entry says it, so it lives here — one sentence,
 * two readers, the same rule as everything else in this file.
 */
export const LIMITS_NOTE = 'Books limit accounts that only take mispriced prices. Mix these with ordinary bets, and expect a limit eventually.';

/**
 * The verdict a screen stamps rather than writes: the red rubber stamp the
 * backtest result screen puts on a strategy with a backtest's worth of legs
 * taken at prices the close beat. Null for every other verdict — "Early days"
 * is not a finding, and a stamp is the app shouting.
 */
export const evidenceStamp = (evidence) => (evidence?.verdict === 'no-edge' ? 'No edge' : null);

export const MODEL_LABELS = {
  none: 'None',
  'market-consensus': 'Market consensus',
  'sharp-anchor': 'Sharp anchor',
  elo: 'Elo',
  'dixon-coles': 'Dixon–Coles',
  'close-line': 'Close line',
  'squiggle-ensemble': 'Squiggle',
  'cricket-toss': 'Cricket toss',
  'user-upload': 'Your own',
};

export const DEVIG_LABELS = {
  multiplicative: 'Multiplicative',
  additive: 'Additive',
  power: 'Power',
  shin: 'Shin',
};

export const STAKING_LABELS = {
  flat: 'Flat',
  'fractional-kelly': 'Fractional Kelly',
};

export const BOOKMAKER_LABELS = {
  single: 'Your book',
  'best-price': 'Best price',
  // One book for the whole ticket, chosen for the ticket rather than for any
  // one leg — see `bookmakerRule` in engine/strategy.js.
  'best-book': 'Best book',
};

export const LEG_RULE_LABELS = {
  whelan: 'Whelan',
  fixed: 'Fixed',
  tier: 'Tiers',
  mix: 'Mix',
  singles: 'Singles',
};

const titleCase = (id) => {
  const s = String(id ?? '');
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

/** True when this strategy can be run right now, rather than waiting on a model. */
export const isRunnable = (strategy) =>
  RUNNABLE_MODELS.includes(strategy?.probabilitySource?.model);

/** "Whelan", "2–3 legs", "5 legs · 2 long shots", "Tiers". An en dash, because it is a range. */
export function legRuleLabel(strategy) {
  const rule = strategy?.legRule ?? {};
  if (rule.type === 'whelan') return 'Whelan';
  if (rule.type === 'tier') {
    // The knob is the only thing that separates two tier strategies, so a card
    // that printed "Tiers" for both would be a card that cannot tell them
    // apart. Plain Classic says nothing, because zero long shots is Classic.
    const shots = strategy?.params?.longShots ?? 0;
    if (!Number.isFinite(shots) || shots <= 0) return 'Tiers';
    return `Tiers · ${shots} long shot${shots === 1 ? '' : 's'}`;
  }
  // Not a leg count at all: one leg, and however many of those the day
  // produced. "Up to 3 singles" is the promise the rule actually makes — the
  // board decides whether it prints one, three or none.
  if (rule.type === 'singles') {
    const max = rule.max;
    if (!Number.isFinite(max)) return 'Singles';
    return max === 1 ? '1 single' : `Up to ${max} singles`;
  }
  if (rule.type === 'fixed') {
    const min = strategy?.combiner?.minLegs ?? rule.legs;
    const max = Math.min(rule.legs ?? min, strategy?.combiner?.maxLegs ?? rule.legs ?? min);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'Fixed';
    return min === max ? `${max} legs` : `${min}–${max} legs`;
  }
  if (rule.type === 'mix') {
    // Both halves of the shape, because either alone is a different strategy:
    // five legs with no long shots is not five legs with two.
    const legs = Math.min(rule.legs ?? NaN, strategy?.combiner?.maxLegs ?? rule.legs ?? NaN);
    const shots = rule.longShots;
    if (!Number.isFinite(legs) || !Number.isFinite(shots)) return 'Mix';
    return `${legs} legs · ${shots} long shot${shots === 1 ? '' : 's'}`;
  }
  return LEG_RULE_LABELS[rule.type] ?? titleCase(rule.type);
}

/** "Market consensus · Shin", "Sharp anchor", "None". */
export function sourceLabel(strategy) {
  const source = strategy?.probabilitySource ?? {};
  const model = MODEL_LABELS[source.model] ?? titleCase(source.model);
  if (source.model === 'none') return 'None';
  const devig = source.params?.devig ?? strategy?.activeLeg?.devig;
  const method = DEVIG_LABELS[devig] ?? titleCase(devig);
  return source.model === 'market-consensus' && method ? `${model} · ${method}` : model;
}

const VULGAR = { 0.25: '¼', 0.5: '½', 0.75: '¾', 0.2: '⅕', 0.1: '⅒' };

/** "Flat" or "¼ Kelly, 2% cap". */
export function stakingLabel(strategy) {
  const staking = strategy?.staking ?? {};
  if (staking.type !== 'fractional-kelly') return STAKING_LABELS[staking.type] ?? 'Flat';
  const fraction = VULGAR[staking.fraction] ?? `${Number(staking.fraction ?? 1)}×`;
  const cap = Number.isFinite(staking.capPct) ? `, ${+(staking.capPct * 100).toFixed(2)}% cap` : '';
  return `${fraction} Kelly${cap}`;
}

/** "Your book" or "Best price". */
export const bookmakerLabel = (strategy) =>
  BOOKMAKER_LABELS[strategy?.bookmakerRule] ?? titleCase(strategy?.bookmakerRule);

/** "1 ticket a day" / "4 tickets a day". */
export function ticketsLabel(strategy) {
  const tickets = strategy?.slots ?? 1;
  return `${tickets} ticket${tickets === 1 ? '' : 's'} a day`;
}

/** "Every sport" or "AFL, NRL" — an empty filter means all of them. */
export function sportsLabel(strategy) {
  const sports = strategy?.filters?.sports ?? [];
  if (!sports.length) return 'Every sport';
  return sports.map((s) => SPORT_LABELS[s] ?? String(s).toUpperCase()).join(', ');
}

/**
 * The ink a strategy card is printed in — the leg rule, not the tier, because a
 * strategy has no tier until it has run. Subtle on purpose: it separates the
 * kinds at a glance and carries no meaning of its own, so nothing is lost to a
 * reader who cannot tell the inks apart. Every kind the app knows takes one of
 * the four inks — `--rule` is a hairline, too faint for the slot count beside
 * it, so it is the fallback for a leg rule out of a later build and nothing
 * else.
 */
export const legRuleInk = (strategy) => ({
  whelan: 'var(--tier-medium)',
  fixed: 'var(--tier-low)',
  tier: 'var(--tier-high)',
  mix: 'var(--tier-outrageous)',
  // The fifth kind takes the first ink: a single IS a fixed leg count, of one,
  // and inventing a fifth colour for the pair would be a colour that means
  // "not one of the four" rather than anything about the strategy.
  singles: 'var(--tier-low)',
}[strategy?.legRule?.type] ?? 'var(--rule)');
